import { useState, useCallback } from 'react';
import { Upload, FileText, X, Camera } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface FileUploadProps {
  onFileUpload: (file: File) => void;
  isProcessing: boolean;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  onFileUpload,
  isProcessing
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (isValidFileType(file)) {
        setSelectedFile(file);
        onFileUpload(file);
      }
    }
  }, [onFileUpload]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (isValidFileType(file)) {
        setSelectedFile(file);
        onFileUpload(file);
      }
    }
  }, [onFileUpload]);

  const isValidFileType = (file: File) => {
    const validTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    return validTypes.includes(file.type) || /\.docx$/i.test(file.name);
  };

  const removeFile = () => {
    setSelectedFile(null);
  };

  if (selectedFile && !isProcessing) {
    return <Card className="p-6 border-2 border-dashed border-success bg-success-light">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="h-8 w-8 text-success" />
            <div>
              <p className="font-medium text-success">{selectedFile.name}</p>
              <p className="text-sm text-muted-foreground">
                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={removeFile} className="text-success hover:text-success/80">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </Card>;
  }

  return <div className={cn("relative border-2 border-dashed border-border bg-background rounded-lg transition-all duration-200", dragActive && "border-primary bg-primary/5", isProcessing && "opacity-50 pointer-events-none")} onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}>
      <div className="p-8 text-center">
        <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold mb-2">Speisekarte hochladen</h3>
        <p className="text-muted-foreground mb-4">
          Ziehen Sie Ihre Getränkekarte/Speisekarte hierher oder klicken Sie zum Auswählen
        </p>
        <p className="text-sm text-muted-foreground mb-6">
          Unterstützte Formate: PDF, JPG, PNG, DOCX (max. 20MB)
        </p>
        
        {/* Hidden file input for selecting files */}
        <input 
          type="file" 
          accept=".pdf,.jpg,.jpeg,.png,.docx" 
          onChange={handleFileInput} 
          className="hidden" 
          id="file-upload" 
          disabled={isProcessing} 
        />
        
        {/* Hidden camera input for taking photos - accept="image/*" required for camera on mobile */}
        <input 
          type="file" 
          accept="image/*" 
          capture="environment"
          onChange={handleFileInput} 
          className="hidden" 
          id="camera-upload" 
          disabled={isProcessing} 
        />
        
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <label htmlFor="file-upload">
            <Button variant="primary" size="lg" className="cursor-pointer w-full sm:w-auto" asChild>
              <span>
                <FileText className="h-4 w-4 mr-2" />
                Datei auswählen
              </span>
            </Button>
          </label>
          
          <label htmlFor="camera-upload">
            <Button variant="outline" size="lg" className="cursor-pointer w-full sm:w-auto" asChild>
              <span>
                <Camera className="h-4 w-4 mr-2" />
                Foto machen
              </span>
            </Button>
          </label>
        </div>
      </div>
    </div>;
};